import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plane, Home, Car, Truck, Phone } from 'lucide-react';

// Contenu spécifique DNA-24 (assistance médicale & voyage). Bilingue EN/FR.
const CONTENT = {
  en: {
    pageTitle: 'Worldwide Assistance Services',
    pageSubtitle: 'Medical Claims management & Concierge services for third parties, available 24/7 across Asia.',
    services: [
      {
        id: 'medical-travel',
        title: 'Medical & Travel Assistance',
        description: 'In case of a medical issue during your travels, our teams are available 24/7 to guide and support you, directing you to the most appropriate healthcare facility.',
        icon: Plane,
      },
      {
        id: 'home-healthcare',
        title: 'Home & Healthcare Services',
        description: 'Comprehensive services designed to support our clients in maintaining a comfortable and secure lifestyle at home.',
        icon: Home,
      },
      {
        id: 'roadside',
        title: 'Roadside Assistance',
        description: 'Fast and reliable roadside assistance to get you back on track wherever you are.',
        icon: Car,
      },
      {
        id: 'complex-logistics',
        title: 'Complex Medical Logistics',
        description: 'Coordination of complex medical logistics, repatriation and operations on behalf of our insurance partners.',
        icon: Truck,
      },
    ],
    ctaTitle: 'Contact our 24/7 Careline',
    ctaSubtitle: 'Medical emergencies and concierge services are our daily business.',
    ctaButton: 'Contact us',
  },
  fr: {
    pageTitle: 'Services d\u2019assistance mondiale',
    pageSubtitle: 'Gestion des sinistres m\u00e9dicaux & services de conciergerie pour des tiers, disponibles 24h/24 et 7j/7 en Asie.',
    services: [
      {
        id: 'medical-travel',
        title: 'Assistance m\u00e9dicale & voyage',
        description: 'En cas de probl\u00e8me m\u00e9dical pendant vos voyages, nos \u00e9quipes sont disponibles 24h/24 pour vous guider et vous orienter vers l\u2019\u00e9tablissement de sant\u00e9 le plus adapt\u00e9.',
        icon: Plane,
      },
      {
        id: 'home-healthcare',
        title: 'Sant\u00e9 & assistance \u00e0 domicile',
        description: 'Des services complets con\u00e7us pour accompagner nos clients dans un quotidien confortable et s\u00e9curis\u00e9 \u00e0 domicile.',
        icon: Home,
      },
      {
        id: 'roadside',
        title: 'Assistance routi\u00e8re',
        description: 'Une assistance routi\u00e8re rapide et fiable pour vous remettre en route o\u00f9 que vous soyez.',
        icon: Car,
      },
      {
        id: 'complex-logistics',
        title: 'Logistique m\u00e9dicale complexe',
        description: 'Coordination de logistiques m\u00e9dicales complexes, rapatriements et op\u00e9rations pour le compte de nos partenaires assureurs.',
        icon: Truck,
      },
    ],
    ctaTitle: 'Contactez notre Careline 24/7',
    ctaSubtitle: 'Les urgences m\u00e9dicales et les services de conciergerie sont notre quotidien.',
    ctaButton: 'Nous contacter',
  },
};

const SolutionsAssistance = () => {
  const { i18n } = useTranslation('public');
  const lang = (i18n.language || 'en').startsWith('fr') ? 'fr' : 'en';
  const C = CONTENT[lang];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="solutions-assistance-page">
      {/* Header Section */}
      <section className="bg-gradient-to-br from-brand-primary to-brand-primary-hover text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold mb-6">{C.pageTitle}</h1>
          <p className="text-xl lg:text-2xl text-white/90 max-w-3xl mx-auto">{C.pageSubtitle}</p>
        </div>
      </section>

      {/* Cards Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {C.services.map((service) => {
              const IconComponent = service.icon;
              return (
                <div
                  key={service.id}
                  data-testid={`assistance-card-${service.id}`}
                  className="group bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden flex flex-col h-full"
                >
                  <div className="p-8 flex flex-col flex-grow">
                    <div className="flex justify-center mb-6">
                      <div className="bg-gray-50 group-hover:bg-brand-primary/10 p-4 rounded-full transition-all duration-300">
                        <IconComponent size={48} className="text-brand-primary transition-colors duration-300" />
                      </div>
                    </div>
                    <h3 className="text-xl font-bold text-brand-text group-hover:text-brand-primary transition-colors duration-300 mb-4 text-center">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 text-center leading-relaxed flex-grow">{service.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA 24/7 Careline */}
      <section className="bg-gradient-to-r from-brand-primary to-brand-primary-hover py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">{C.ctaTitle}</h2>
          <p className="text-xl text-white/90 mb-8">{C.ctaSubtitle}</p>
          <Link
            to="/contact"
            data-testid="assistance-careline-cta"
            className="inline-block bg-white text-brand-primary px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-50 transition-all duration-300 shadow-lg hover:shadow-2xl hover:scale-105 transform hover:-translate-y-1"
          >
            {C.ctaButton}
          </Link>
        </div>
      </section>
    </div>
  );
};

export default SolutionsAssistance;
